import { useCallback, useEffect, useRef, useState } from "react";

import { globalEvents } from "../../../../common/helpers/GlobalEvents";
import { useTimerState } from "../../../../common/hooks/useTimerState";
import { ACCORDION_EVENT } from "./constants";

// Efficient array comparison without JSON.stringify
const arraysEqual = (a: string[], b: string[]): boolean => {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}

	return true;
};

export const useAccordionChangeControl = (
	accordion_id: string,
	selectMultiple: boolean,
	onChange: (values: string[]) => void,
	initValues: string[],
	maxSelection: number
) => {
	const [selected, setSelected] = useState<string[]>([...initValues]);
	const initValuesRef = useRef<string[]>([...initValues]);
	const isInitializing = useRef<boolean>(true);
	const mountedRef = useRef<boolean>(true);

	// Reset refs on mount
	useEffect(() => {
		mountedRef.current = true;
		isInitializing.current = true;
		initValuesRef.current = [...initValues];
		setSelected([...initValues]);

		return () => {
			mountedRef.current = false;
		};
	}, [accordion_id]);

	// Update selected state when initValues changes (for cart edit scenarios)
	useEffect(() => {
		if (!mountedRef.current) return;

		if (!arraysEqual(initValuesRef.current, initValues)) {
			isInitializing.current = true;
			setSelected([...initValues]);
			initValuesRef.current = [...initValues];
		}
	}, [initValues, accordion_id]);

	const [changed, setChanged] = useState<boolean>(false);

	const changeHandler = useCallback(
		(evt: React.SyntheticEvent, expand: boolean) => {
			const event = expand ? ACCORDION_EVENT.EXPAND : ACCORDION_EVENT.COLLAPSE;

			globalEvents.emit(event, accordion_id);
		},
		[accordion_id]
	);

	const addSelection = useCallback(
		(selection_id: string) => {
			setSelected((prev) => {
				if (prev.length >= maxSelection) return prev;
				const newprev = [...prev];

				newprev.push(selection_id); // add

				return newprev;
			});
		},
		[maxSelection]
	);

	const removeSelection = useCallback((selection_id: string) => {
		setSelected((prev) => {
			const newprev = [...prev];
			const idx = newprev.indexOf(selection_id);

			if (idx < 0) return prev;
			newprev.splice(idx, 1); // remove

			return newprev;
		});
	}, []);

	const toggleSelection = useCallback(
		(selection_id: string) => {
			setSelected((prev) => {
				const newprev = [...prev];
				const idx = newprev.indexOf(selection_id);

				if (idx < 0) {
					// item not found, add it!
					if (prev.length >= maxSelection) return prev; // we're at max, do nothing
					newprev.push(selection_id);

					return newprev;
				}
				newprev.splice(idx, 1); // remove

				return newprev;
			});
		},
		[maxSelection]
	);

	const setSelection = useCallback((selection_id: string) => {
		setSelected([selection_id]);
	}, []);

	const onClick = useCallback(
		(selection_id: string, allowMultiplePerItem: boolean) => {
			let selectfn = setSelection;

			if (selectMultiple) {
				selectfn = allowMultiplePerItem ? addSelection : toggleSelection;
			}
			const clickHandler = () => {
				isInitializing.current = false;
				selectfn(selection_id);
				setChanged(true);
			};

			return clickHandler;
		},
		[selectMultiple, toggleSelection, addSelection, setSelection]
	);

	const allowOnChange = useTimerState(300);

	useEffect(() => {
		if (!allowOnChange || !mountedRef.current) return;

		let timer: NodeJS.Timeout | undefined;

		// Only call onChange if this is not during initialization
		if (!isInitializing.current) {
			onChange(selected);
			if (!selectMultiple) {
				globalEvents.emit(ACCORDION_EVENT.COLLAPSE, accordion_id);
			}
		} else {
			// Mark initialization as complete after first render
			timer = setTimeout(() => {
				if (mountedRef.current) {
					isInitializing.current = false;
				}
			}, 100);
		}

		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [selected, allowOnChange, selectMultiple, accordion_id, onChange]);

	return {
		changeHandler,
		onClick,
		selected,
		addSelection,
		removeSelection,
		toggleSelection,
		changed,
	};
};
